import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Plus, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const inputCls =
  "w-full rounded-xl bg-white border border-black/10 px-4 py-2.5 text-sm focus:border-[#c9006f] transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#121212]/45 mb-1.5";

const handleAuthError = (e) => {
  if (e?.response?.status === 401) {
    localStorage.removeItem("gb_admin_token");
    window.location.reload();
  }
};

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.type === "image/gif" || file.size < 1024 * 1024) return resolve(file);
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 2000;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const isPng = file.type === "image/png";
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (blob && blob.size < file.size) {
            const name = isPng ? file.name : file.name.replace(/\.\w+$/, "") + ".jpg";
            resolve(new File([blob], name, { type: blob.type }));
          } else {
            resolve(file);
          }
        },
        isPng ? "image/png" : "image/jpeg",
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

const ACCEPT = "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime";
const isVid = (src) => /\.(mp4|webm|mov)(\?|$)/i.test(src || "");
const EMPTY_DRAFT = { badge: "New Service", title: "", desc: "", imgs: [] };

const MediaThumb = ({ src, onRemove, testid }) => (
  <div className="relative group">
    {isVid(src) ? (
      <video src={src} muted preload="metadata" className="w-full h-20 object-cover rounded-lg bg-black/5" />
    ) : (
      <img src={src} alt="Service media" className="w-full h-20 object-cover rounded-lg bg-black/5" />
    )}
    <button
      onClick={onRemove}
      data-testid={testid}
      aria-label="Remove media"
      className="absolute -top-1.5 -right-1.5 grid place-items-center w-6 h-6 rounded-full bg-red-500 text-white shadow hover:bg-red-600 transition-colors"
    >
      <X size={12} />
    </button>
  </div>
);

export const ServicesTab = ({ token, refreshContent }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState("");
  const [deleting, setDeleting] = useState("");
  const [uploadTarget, setUploadTarget] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const addRef = useRef(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [draftUploading, setDraftUploading] = useState(false);
  const draftRef = useRef(null);

  const load = useCallback(() => axios.get(`${API}/content/services`).then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const update = (id, k, v) => setItems((arr) => arr.map((s) => (s.id === id ? { ...s, [k]: v } : s)));

  const uploadFiles = async (files) => {
    const urls = [];
    for (const file of files) {
      const toUpload = await compressImage(file);
      const fd = new FormData();
      fd.append("file", toUpload);
      const { data } = await axios.post(`${API}/upload`, fd, auth(token));
      urls.push(data.url);
    }
    return urls;
  };

  const putService = (s, imgs) =>
    axios.put(`${API}/content/services/${s.id}`, { badge: s.badge, title: s.title, desc: s.desc, imgs }, auth(token));

  const save = async (s) => {
    if (!s.title.trim()) return toast.error("Title is required");
    setSaving(s.id);
    try {
      await putService(s, s.imgs || []);
      toast.success("Service saved");
      refreshContent();
    } catch (e) { handleAuthError(e); toast.error("Save failed"); }
    finally { setSaving(""); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This removes it from the homepage.`)) return;
    setDeleting(s.id);
    try {
      await axios.delete(`${API}/content/services/${s.id}`, auth(token));
      toast.success("Service deleted");
      load(); refreshContent();
    } catch (e) { handleAuthError(e); toast.error("Delete failed"); }
    finally { setDeleting(""); }
  };

  const startAddMedia = (id) => {
    setUploadTarget(id);
    setTimeout(() => addRef.current?.click(), 0);
  };

  const addMediaFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const item = items.find((s) => s.id === uploadTarget);
    if (!files.length || !item) { setUploadTarget(""); return; }
    setUploadBusy(true);
    try {
      const urls = await uploadFiles(files);
      const imgs = [...(item.imgs || []), ...urls];
      await putService(item, imgs);
      toast.success(`${urls.length} media added`);
      load(); refreshContent();
    } catch (err) {
      handleAuthError(err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
      setUploadTarget("");
      if (addRef.current) addRef.current.value = "";
    }
  };

  const removeMedia = async (s, i) => {
    const imgs = (s.imgs || []).filter((_, idx) => idx !== i);
    try {
      await putService(s, imgs);
      toast.success("Media removed");
      load(); refreshContent();
    } catch (e) { handleAuthError(e); toast.error("Remove failed"); }
  };

  const draftUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setDraftUploading(true);
    try {
      const urls = await uploadFiles(files);
      setDraft((d) => ({ ...d, imgs: [...d.imgs, ...urls] }));
      toast.success(`${urls.length} media uploaded`);
    } catch (err) {
      handleAuthError(err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setDraftUploading(false);
      if (draftRef.current) draftRef.current.value = "";
    }
  };

  const addService = async () => {
    if (!draft.title.trim()) return toast.error("Title is required");
    setAdding(true);
    try {
      await axios.post(`${API}/content/services`, draft, auth(token));
      toast.success("Service added");
      setDraft(EMPTY_DRAFT);
      load(); refreshContent();
    } catch (e) { handleAuthError(e); toast.error("Add failed"); }
    finally { setAdding(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-services-tab">
      <input ref={addRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={addMediaFiles} data-testid="admin-service-add-media-input" />
      <input ref={draftRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={draftUpload} data-testid="admin-new-service-file-input" />
      <p className="text-sm text-[#121212]/50">
        These appear in the "Services & Features" section on the homepage. Add multiple photos/videos per service — they auto-cycle in an elegant slideshow on the site.
      </p>
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-black/5 overflow-hidden shadow-sm" data-testid={`admin-service-card-${s.id}`}>
            <div className="p-4 space-y-3">
              <div>
                <span className={labelCls}>Media ({(s.imgs || []).length}) — cycles on the homepage</span>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {(s.imgs || []).map((src, i) => (
                    <MediaThumb key={`${src}-${i}`} src={src} onRemove={() => removeMedia(s, i)} testid={`admin-service-media-remove-${s.id}-${i}`} />
                  ))}
                  <button
                    onClick={() => startAddMedia(s.id)}
                    disabled={uploadBusy}
                    data-testid={`admin-service-add-media-btn-${s.id}`}
                    className="h-20 rounded-lg border-2 border-dashed border-[#91008d]/30 text-[#91008d] grid place-items-center hover:bg-[#91008d]/5 transition-colors disabled:opacity-60"
                    aria-label="Add media"
                  >
                    {uploadBusy && uploadTarget === s.id ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  </button>
                </div>
              </div>
              <div><span className={labelCls}>Badge</span><input className={inputCls} value={s.badge} onChange={(e) => update(s.id, "badge", e.target.value)} data-testid={`admin-service-badge-${s.id}`} /></div>
              <div><span className={labelCls}>Title</span><input className={inputCls} value={s.title} onChange={(e) => update(s.id, "title", e.target.value)} data-testid={`admin-service-title-${s.id}`} /></div>
              <div><span className={labelCls}>Description</span><textarea className={`${inputCls} resize-none`} rows={3} value={s.desc} onChange={(e) => update(s.id, "desc", e.target.value)} data-testid={`admin-service-desc-${s.id}`} /></div>
              <div className="flex gap-2">
                <button onClick={() => save(s)} disabled={saving === s.id} data-testid={`admin-service-save-${s.id}`} className="btn-gold flex-1 rounded-full py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  {saving === s.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
                <button onClick={() => remove(s)} disabled={deleting === s.id} data-testid={`admin-service-delete-${s.id}`} className="rounded-full border border-red-200 text-red-500 px-4 py-2.5 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-60">
                  {deleting === s.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm space-y-3" data-testid="admin-new-service-form">
        <p className="font-serif text-lg text-[#121212]">New Service</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><span className={labelCls}>Badge</span><input className={inputCls} value={draft.badge} onChange={(e) => setDraft({ ...draft, badge: e.target.value })} placeholder="New Service" data-testid="admin-new-service-badge" /></div>
          <div><span className={labelCls}>Title</span><input className={inputCls} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="NEW! My Awesome Service" data-testid="admin-new-service-title" /></div>
        </div>
        <div><span className={labelCls}>Description</span><textarea className={`${inputCls} resize-none`} rows={3} value={draft.desc} onChange={(e) => setDraft({ ...draft, desc: e.target.value })} placeholder="Describe the new service or feature..." data-testid="admin-new-service-desc" /></div>
        {draft.imgs.length > 0 && (
          <div className="grid grid-cols-6 gap-2">
            {draft.imgs.map((src, i) => (
              <MediaThumb key={`${src}-${i}`} src={src} onRemove={() => setDraft((d) => ({ ...d, imgs: d.imgs.filter((_, idx) => idx !== i) }))} testid={`admin-new-service-media-remove-${i}`} />
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => draftRef.current?.click()} disabled={draftUploading} data-testid="admin-new-service-upload-btn" className="inline-flex items-center gap-2 rounded-full border border-[#91008d]/30 text-[#91008d] px-5 py-2.5 text-sm font-semibold hover:bg-[#91008d] hover:text-white transition-colors disabled:opacity-60">
            {draftUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload photos / videos
          </button>
          <button onClick={addService} disabled={adding} data-testid="admin-new-service-add-btn" className="btn-gold ml-auto rounded-full px-7 py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Service
          </button>
        </div>
      </div>
    </div>
  );
};
